import {Injectable, inject, PLATFORM_ID, afterNextRender} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {environment} from "../../../environments/environment";
import {map, Observable} from "rxjs";
import {MinizaemJwt} from "../models/minizaem-jwt";
import {SharedStateService} from "./shared-state.service";
import {NomenclatureItemModel} from "../models/nomenclature-item-model";
import {jwtDecode} from "jwt-decode";
import {isPlatformBrowser} from "@angular/common";
import {CookieService} from "ngx-cookie-service";

@Injectable({
  providedIn: 'root'
})
export class MinizaemApiClientService {
  protected http = inject(HttpClient);
  protected sharedState = inject(SharedStateService);
  protected platformId = inject(PLATFORM_ID);

  protected jwt: string | null = null;
  protected apiDomain: string = environment.minizaemApiDomain;
  protected cookieService = inject(CookieService);

  constructor() {
    if (this.cookieService.check('minizaemApiJwt')) {
      this.jwt = this.cookieService.get('minizaemApiJwt');
    }
  }

// Helper to determine cookie settings
  protected get cookieOptions() {
    return {
      path: '/',
      sameSite: 'Lax' as const,
      // Secure should be true in production (HTTPS)
      secure: isPlatformBrowser(this.platformId) ? window.location.protocol === 'https:' : false
    };
  }

  setJwt(jwt: string | null): void {
    if (jwt == null) {
      this.cookieService.delete("minizaemApiJwt", '/');
      this.sharedState.triggerLogoutActions();
    } else {
      this.cookieService.set("minizaemApiJwt", jwt, this.cookieOptions);
    }

    this.jwt = jwt;
  }

  getJwt(): string | null {
    if (this.jwt) {
      if (this.isJwtExpired()) {
        this.setJwt(null);
      } else {
        if (!this.sharedState.isCustomerLogged()) {
          this.sharedState.isCustomerLogged.set(true);
        }
      }
    }

    return this.jwt;
  }

  refreshToken(email: string, password: string): Observable<any> {
    return this.http.put(this.buildURI("api/customer/refresh-jwt")
      , {
        "email": email,
        "password": password
      }
    );
  }

  getSystemDateTime(): Observable<any> {
    return this.http.get(this.buildURI("api/system-date-time")
      , {}
    );
  }

  getNomenclature(nomenclatureName: string): Observable<NomenclatureItemModel[]> {
    return this.http.get(this.buildURI(`api/nomenclature/${nomenclatureName}`)
      , {}
    ).pipe(
      map((response): NomenclatureItemModel[] => {
        return <NomenclatureItemModel[]>response;
      })
    );
  }

  getAddressCityAutocompleteOptions(term: string): Observable<NomenclatureItemModel[]> {
    return this.http.get(this.buildURI(`api/address-city-autocomplete-options/${term}`)
      , {}
    ).pipe(
      map((response): NomenclatureItemModel[] => {
        return <NomenclatureItemModel[]>response;
      })
    );
  }

  getJwtPayload(): MinizaemJwt | null {
    if (this.jwt !== null) {
      const minizaemJwt = jwtDecode<MinizaemJwt>(this.jwt);
      if (minizaemJwt !== undefined) {
        return minizaemJwt;
      }
    }

    return null;
  }

  protected buildURI(route: string): string {
    return this.apiDomain + route;
  }

  protected isJwtExpired(): boolean {
    const jwtPayload = this.getJwtPayload();

    if (jwtPayload !== null) {
      const currentDate = new Date();
      const expirationDateInSeconds = jwtPayload.exp;
      const currentTimeInSeconds = Math.trunc(currentDate.getTime() / 1000);

      return expirationDateInSeconds < currentTimeInSeconds;
    }

    return false;
  }
}
